import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CpmBadge, CpmButton, CpmChip, CpmFilterDropdown } from '../../../components/design-system';
import { listStates, listTemplateTags, listTemplates } from '../../../services/reportsAdminService';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type {
    ActiveStatus,
    PlaceLoadState,
    TemplateListRow,
} from '../../../types/reportsAdmin.types';
import { ACTIVE_STATUSES } from '../../../types/reportsAdmin.types';
import { REPORTS_COPY, REPORTS_TIPS } from '../copy';
import { searchQueryFrom, stateName } from '../helpers';
import AppliedFilterChips, { type AppliedChip } from '../components/AppliedFilterChips';
import FilteredEmpty from '../components/FilteredEmpty';
import InfiniteScrollSentinel from '../components/InfiniteScrollSentinel';
import InfoI from '../components/InfoI';
import PencilButton from '../components/PencilButton';
import PlaceStateFrames from '../components/PlaceStateFrames';
import { ReportsWait } from '../components/ReportsDialog';
import SearchField from '../components/SearchField';
import StickySortHead from '../components/StickySortHead';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import ApplyTemplateModal from './ApplyTemplateModal';
import CopyFromStoreBrandModal from './CopyFromStoreBrandModal';
import TemplateEditModal from './TemplateEditModal';

const COLS = '2.3fr 1.5fr 1.4fr 0.85fr 0.9fr 150px';

interface FilterState {
    search: string;
    states: string[];
    tags: string[];
    activeStatus: ActiveStatus[];
}

const EMPTY: FilterState = { search: '', states: [], tags: [], activeStatus: [] };

export default function TemplatesBody() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = useState<FilterState>(EMPTY);
    const debouncedSearch = useDebouncedValue(filters.search, 300);
    const searchQuery = searchQueryFrom(debouncedSearch);
    const [sort, setSort] = useState<{ key: 'name' | 'reportCount'; dir: 'asc' | 'desc' }>({
        key: 'name',
        dir: 'asc',
    });
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState<TemplateListRow[]>([]);
    const [total, setTotal] = useState(0);
    const [place, setPlace] = useState<PlaceLoadState>('idle');
    const [states, setStates] = useState<CpmState[]>([]);
    const [tagOptions, setTagOptions] = useState<string[]>([]);
    const [tagsError, setTagsError] = useState(false);
    const [statesError, setStatesError] = useState(false);
    const [editId, setEditId] = useState<string | null>(searchParams.get('edit'));
    const [copyOpen, setCopyOpen] = useState(false);
    const [applyId, setApplyId] = useState<string | null>(null);
    const [listBusy, setListBusy] = useState(false);
    const [moreBusy, setMoreBusy] = useState(false);
    const [moreFailed, setMoreFailed] = useState(false);
    const readyOnce = useRef(false);
    const allTotalRef = useRef(0);

    const hasFilters =
        Boolean(searchQuery) ||
        filters.states.length > 0 ||
        filters.tags.length > 0 ||
        filters.activeStatus.length > 0;

    const loadOptions = useCallback(async () => {
        setStatesError(false);
        setTagsError(false);
        try {
            setStates(await listStates());
        } catch {
            setStatesError(true);
        }
        try {
            setTagOptions(await listTemplateTags());
        } catch {
            setTagsError(true);
        }
    }, []);

    const load = useCallback(
        async (nextPage: number, append: boolean) => {
            if (!append) {
                setMoreFailed(false);
                if (readyOnce.current) setListBusy(true);
                else setPlace('loading');
            } else {
                setMoreBusy(true);
                setMoreFailed(false);
            }
            try {
                const data = await listTemplates({
                    search: searchQuery || undefined,
                    states: filters.states.length ? filters.states : undefined,
                    tags: filters.tags.length ? filters.tags : undefined,
                    activeStatus:
                        filters.activeStatus.length === 1 ? filters.activeStatus[0] : undefined,
                    sort: sort.key,
                    sortDir: sort.dir,
                    page: nextPage,
                    limit: 25,
                });
                setRows((prev) => (append ? [...prev, ...data.templates] : data.templates));
                setTotal(data.total);
                setPage(nextPage);
                readyOnce.current = true;
                setPlace('ready');
            } catch {
                if (append) setMoreFailed(true);
                else if (!readyOnce.current) setPlace('error');
            } finally {
                setListBusy(false);
                setMoreBusy(false);
            }
        },
        [searchQuery, filters.states, filters.tags, filters.activeStatus, sort]
    );

    useEffect(() => {
        void loadOptions();
    }, [loadOptions]);

    useEffect(() => {
        void load(1, false);
    }, [load]);

    useEffect(() => {
        if (searchParams.get('edit')) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('edit');
                return next;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const chips: AppliedChip[] = [
        ...filters.states.map((state) => ({
            key: `st-${state}`,
            label: state,
            onClear: () =>
                setFilters({ ...filters, states: filters.states.filter((item) => item !== state) }),
        })),
        ...filters.tags.map((tag) => ({
            key: `tg-${tag}`,
            label: tag,
            onClear: () =>
                setFilters({ ...filters, tags: filters.tags.filter((item) => item !== tag) }),
        })),
        ...filters.activeStatus.map((status) => ({
            key: `stt-${status}`,
            label: status,
            onClear: () =>
                setFilters({
                    ...filters,
                    activeStatus: filters.activeStatus.filter((item) => item !== status),
                }),
        })),
    ];

    const trueEmpty = place === 'ready' && total === 0 && !hasFilters;
    const filteredEmpty = place === 'ready' && total === 0 && hasFilters;
    const showBody = (place === 'ready' && !trueEmpty) || listBusy;
    if (!hasFilters && place === 'ready') allTotalRef.current = total;
    const showingText = hasFilters
        ? `${total} of ${allTotalRef.current || total} templates`
        : `${total} templates`;
    const inactiveN = rows.filter((row) => row.activeStatus === 'Inactive').length;
    const tagNames = new Set(rows.flatMap((row) => row.tags || []));
    const emptyN = rows.filter((row) => (row.reportCount || 0) === 0).length;
    const metaText = `${inactiveN} inactive · ${tagNames.size} tags in use${
        emptyN ? ` · ${emptyN} hold no reports` : ''
    }`;
    const actionsDisabled = listBusy;

    return (
        <div>
            {showBody ? (
                <div style={{ padding: '16px 20px 22px' }}>
                    <div className="flex flex-wrap items-center" style={{ gap: 9 }}>
                        <SearchField
                            value={filters.search}
                            onChange={(search) => setFilters({ ...filters, search })}
                            placeholder="Search a template by name or tag"
                            width={306}
                            size="sm"
                        />
                        <CpmFilterDropdown
                            label="State"
                            triggerVariant="chip"
                            portal
                            disabled={actionsDisabled}
                            menuMinWidth={286}
                            failed={statesError}
                            failedTitle={REPORTS_COPY.LOAD_FAIL_STATES}
                            failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                            onRetry={() => void loadOptions()}
                            options={states
                                .map(stateName)
                                .filter(Boolean)
                                .map((name) => ({ value: name, label: name }))}
                            selected={filters.states}
                            onChange={(next) => setFilters({ ...filters, states: next })}
                        />
                        <CpmFilterDropdown
                            label="Tag"
                            triggerVariant="chip"
                            portal
                            disabled={actionsDisabled}
                            menuMinWidth={286}
                            failed={tagsError}
                            failedTitle={REPORTS_COPY.LOAD_FAIL_TAGS}
                            failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                            onRetry={() => void loadOptions()}
                            options={tagOptions.map((tag) => ({ value: tag, label: tag }))}
                            selected={filters.tags}
                            onChange={(next) => setFilters({ ...filters, tags: next })}
                        />
                        <CpmFilterDropdown
                            label="Status"
                            triggerVariant="chip"
                            portal
                            disabled={actionsDisabled}
                            options={ACTIVE_STATUSES.map((status) => ({
                                value: status,
                                label: status,
                            }))}
                            selected={filters.activeStatus}
                            onChange={(next) =>
                                setFilters({ ...filters, activeStatus: next as ActiveStatus[] })
                            }
                        />
                        <div className="flex items-center" style={{ gap: 10, marginLeft: 'auto' }}>
                            <InfoI
                                title={REPORTS_TIPS.TEMPLATE.title}
                                body="Applying a template turns these reports on for the store brands you pick. Changing the template later does not change store brands you already did."
                            />
                            <button
                                type="button"
                                disabled={actionsDisabled}
                                onClick={() => setCopyOpen(true)}
                                className="bg-transparent border-0"
                                style={{
                                    padding: 0,
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                    letterSpacing: '-0.01em',
                                    color: 'var(--cpm-text-secondary)',
                                    cursor: actionsDisabled ? 'not-allowed' : 'pointer',
                                    opacity: actionsDisabled ? 0.5 : 1,
                                }}
                                onMouseEnter={(e) => {
                                    if (actionsDisabled) return;
                                    e.currentTarget.style.color = 'var(--cpm-brand-navy)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = 'var(--cpm-text-secondary)';
                                }}
                            >
                                {REPORTS_COPY.COPY_FROM_BRAND}
                            </button>
                            <CpmButton
                                variant="outline"
                                size="sm"
                                disabled={actionsDisabled}
                                onClick={() => navigate('/reports/templates/new')}
                            >
                                {REPORTS_COPY.NEW_TEMPLATE}
                            </CpmButton>
                        </div>
                    </div>
                    <AppliedFilterChips
                        chips={chips}
                        onClearAll={() => setFilters({ ...EMPTY, search: filters.search })}
                    />
                    <div
                        className="flex items-baseline justify-between flex-wrap"
                        style={{ gap: 16, margin: '16px 0 10px' }}
                    >
                        <div
                            style={{
                                fontSize: 12.5,
                                fontWeight: 800,
                                color: 'var(--cpm-text-primary)',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {showingText}
                        </div>
                        <div
                            style={{
                                fontSize: 11.5,
                                fontWeight: 600,
                                color: 'var(--cpm-text-secondary)',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {metaText}
                        </div>
                    </div>
                    {listBusy ? (
                        <div>
                            <div style={{ paddingBottom: 14 }}>
                                <ReportsWait label="Matching the templates to that search and those filters…" />
                            </div>
                            <div
                                className="overflow-hidden"
                                style={{
                                    border: '1px solid var(--cpm-border-default)',
                                    borderRadius: 10,
                                }}
                            >
                                {Array.from({ length: 4 }, (_, i) => (
                                    <div
                                        key={i}
                                        className="cpm-sk"
                                        style={{ height: 56, borderRadius: 0 }}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : filteredEmpty ? (
                        <FilteredEmpty
                            title="No template matches what you have on"
                            cause="The search and filters caused this. Templates do exist."
                            restated={searchQuery ? `The search is “${searchQuery}”.` : undefined}
                            totalInPlace={`${allTotalRef.current || total} templates are in the list with nothing on`}
                            onClear={() => setFilters(EMPTY)}
                        />
                    ) : (
                        <div
                            className="overflow-auto"
                            style={{
                                maxHeight: 580,
                                border: '1px solid var(--cpm-border-default)',
                                borderRadius: 10,
                            }}
                        >
                            <div
                                className="sticky top-0 z-[3] grid"
                                style={{ gridTemplateColumns: COLS, minWidth: 920 }}
                            >
                                <StickySortHead
                                    label="Template"
                                    sortable
                                    active={sort.key === 'name'}
                                    dir={sort.dir}
                                    onSort={() =>
                                        setSort((prev) =>
                                            prev.key === 'name'
                                                ? {
                                                      key: 'name',
                                                      dir: prev.dir === 'asc' ? 'desc' : 'asc',
                                                  }
                                                : { key: 'name', dir: 'asc' }
                                        )
                                    }
                                />
                                <StickySortHead label="Tags" />
                                <StickySortHead label="States" />
                                <StickySortHead
                                    label="Reports"
                                    align="right"
                                    sortable
                                    active={sort.key === 'reportCount'}
                                    dir={sort.dir}
                                    onSort={() =>
                                        setSort((prev) =>
                                            prev.key === 'reportCount'
                                                ? {
                                                      key: 'reportCount',
                                                      dir: prev.dir === 'asc' ? 'desc' : 'asc',
                                                  }
                                                : { key: 'reportCount', dir: 'asc' }
                                        )
                                    }
                                />
                                <StickySortHead label="Status" />
                                <StickySortHead label="" align="right" />
                            </div>
                            {rows.map((row) => (
                                <div
                                    key={row._id}
                                    className="grid items-center cursor-pointer"
                                    style={{
                                        gridTemplateColumns: COLS,
                                        minWidth: 920,
                                        borderTop: '1px solid var(--cpm-border-default)',
                                    }}
                                    onClick={() => setEditId(row._id)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background =
                                            'var(--cpm-purple-tint-3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                    }}
                                >
                                    <div style={{ padding: '12px 16px', minWidth: 0 }}>
                                        <div
                                            style={{
                                                fontSize: 13.5,
                                                fontWeight: 800,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {row.name}
                                        </div>
                                        {row.reportCount === 0 ? (
                                            <div
                                                style={{
                                                    marginTop: 3,
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: 'var(--cpm-text-tertiary)',
                                                }}
                                            >
                                                Empty template — allowed
                                            </div>
                                        ) : null}
                                    </div>
                                    <div
                                        className="flex flex-wrap"
                                        style={{ gap: 4, padding: '12px 16px' }}
                                    >
                                        {row.tags.length === 0 ? (
                                            <span
                                                style={{
                                                    fontSize: 12,
                                                    color: 'var(--cpm-text-tertiary)',
                                                }}
                                            >
                                                {REPORTS_COPY.NO_TAGS}
                                            </span>
                                        ) : (
                                            row.tags.map((tag) => (
                                                <CpmChip key={tag} variant="tag">
                                                    {tag}
                                                </CpmChip>
                                            ))
                                        )}
                                    </div>
                                    <div style={{ padding: '12px 16px' }}>
                                        {row.allStates ? (
                                            <span
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: 'var(--cpm-text-secondary)',
                                                }}
                                            >
                                                {REPORTS_COPY.ALL_STATES}
                                            </span>
                                        ) : (
                                            <div className="flex flex-wrap" style={{ gap: 4 }}>
                                                {row.states.map((state) => (
                                                    <CpmChip key={state} variant="tag">
                                                        {state}
                                                    </CpmChip>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ padding: '12px 16px', textAlign: 'right' }}>
                                        <div
                                            style={{
                                                fontSize: 13.5,
                                                fontWeight: 800,
                                                fontVariantNumeric: 'tabular-nums',
                                            }}
                                        >
                                            {row.reportCount}
                                        </div>
                                        {row.reportCount === 0 ? (
                                            <div
                                                className="uppercase"
                                                style={{
                                                    marginTop: 2,
                                                    fontSize: 9.5,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.06em',
                                                    color: 'var(--cpm-text-tertiary)',
                                                }}
                                            >
                                                {REPORTS_COPY.NONE_IN_IT}
                                            </div>
                                        ) : null}
                                    </div>
                                    <div style={{ padding: '12px 16px' }}>
                                        <CpmBadge
                                            tone={
                                                row.activeStatus === 'Active'
                                                    ? 'success'
                                                    : 'neutral'
                                            }
                                            dot
                                        >
                                            {row.activeStatus}
                                        </CpmBadge>
                                    </div>
                                    <div
                                        className="flex items-center justify-end"
                                        style={{ gap: 6, padding: '12px 16px 12px 0' }}
                                    >
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/reports/templates/${row._id}/duplicate`);
                                            }}
                                            className="bg-transparent border-0 cursor-pointer"
                                            style={{
                                                padding: 0,
                                                fontSize: 12.5,
                                                fontWeight: 800,
                                                letterSpacing: '-0.01em',
                                                color: 'var(--cpm-text-secondary)',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.color =
                                                    'var(--cpm-brand-navy)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.color =
                                                    'var(--cpm-text-secondary)';
                                            }}
                                        >
                                            {REPORTS_COPY.DUPLICATE}
                                        </button>
                                        <PencilButton
                                            label="Open this template"
                                            onClick={() => setEditId(row._id)}
                                        />
                                    </div>
                                </div>
                            ))}
                            <InfiniteScrollSentinel
                                hasMore={rows.length < total}
                                loading={moreBusy}
                                error={moreFailed}
                                onLoadMore={() => void load(page + 1, true)}
                                showEnd={rows.length > 0}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <PlaceStateFrames
                    state={place}
                    trueEmpty={trueEmpty}
                    loadingLabel="Waiting for the templates. Blank here does not mean no templates exist."
                    emptyTitle="No templates have been made yet"
                    emptyBody="Reports may well exist in the catalogue. What is missing is a template: a named set of reports that ops can apply to a store brand in one go."
                    emptyAction={
                        <CpmButton
                            variant="navy"
                            size="lg"
                            style={{ height: 42 }}
                            onClick={() => navigate('/reports/templates/new')}
                        >
                            {REPORTS_COPY.NEW_TEMPLATE}
                        </CpmButton>
                    }
                    emptyNote={
                        <>
                            Pick the reports a template holds from the catalogue.{' '}
                            <Link
                                to="/reports/catalog"
                                style={{ color: 'var(--cpm-brand-purple)', fontWeight: 800 }}
                            >
                                Go to Catalogue
                            </Link>
                        </>
                    }
                    errorTitle="Couldn't load the templates"
                    errorBody="The template list did not come back. This says nothing about how many templates exist, and the catalogue was not part of this request."
                    retryLabel="Retry the templates"
                    skeletonRows={3}
                    onRetry={() => void load(1, false)}
                >
                    {null}
                </PlaceStateFrames>
            )}
            {editId ? (
                <TemplateEditModal
                    id={editId}
                    stackedOpen={Boolean(applyId)}
                    onClose={() => setEditId(null)}
                    onSaved={() => void load(1, false)}
                    onApply={() => {
                        setApplyId(editId);
                    }}
                />
            ) : null}
            {copyOpen ? (
                <CopyFromStoreBrandModal
                    onClose={() => setCopyOpen(false)}
                    onCreated={(row) => {
                        setCopyOpen(false);
                        setEditId(row._id);
                        void load(1, false);
                    }}
                />
            ) : null}
            {applyId ? (
                <ApplyTemplateModal
                    source="templates"
                    templateId={applyId}
                    onClose={() => setApplyId(null)}
                    onApplied={() => {
                        setApplyId(null);
                        void load(1, false);
                    }}
                />
            ) : null}
        </div>
    );
}
